import { useQuery } from '@tanstack/react-query';
import { SensorsResource, SensorStreamTokenResponse } from '@mark43/cad-api';
import { useApiClient } from '~/api-client';
import { SENSOR_TYPE_VERKADA_CAMERA } from './constants';
import { useSensorsEnabled } from './use-sensors-enabled';

/** The token is valid 30 minutes and is not refreshable, so re-fetch with margin to spare. */
const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;
const DEFAULT_TOKEN_LIFETIME_MS = 30 * 60 * 1000;
/**
 * Floor for the refetch interval. `millisUntilTokenRefresh` returns 0 for an already-expired
 * token, and React Query treats a 0ms `refetchInterval` as a 0ms timer, not as "off" — so without
 * this an expired token would refetch in a tight loop.
 */
const MIN_TOKEN_REFETCH_INTERVAL_MS = 30 * 1000;
const CAMERA_ID_PLACEHOLDER = '{cameraId}';

/**
 * Milliseconds until the token should be re-fetched.
 *
 * `expiresAtUtc` is **epoch milliseconds**, not an ISO string — a Java `long`, so it is always
 * present but can still be zero or nonsensical if the vendor sent no expiry and the backend's own
 * fallback did not apply. Anything non-positive falls back to a conservative lifetime rather than
 * refetching in a loop.
 */
export function millisUntilTokenRefresh(
    token: SensorStreamTokenResponse,
    nowMs: number
): number {
    const expiresAtMs = token.expiresAtUtc;
    if (!Number.isFinite(expiresAtMs) || expiresAtMs <= 0) {
        return DEFAULT_TOKEN_LIFETIME_MS - TOKEN_REFRESH_MARGIN_MS;
    }
    return Math.max(0, expiresAtMs - nowMs - TOKEN_REFRESH_MARGIN_MS);
}

/**
 * The backend hands back a fully formed HLS URL with the literal `{cameraId}` left in place — org
 * id and jwt are already substituted, and `codec=h264` is already pinned (Verkada defaults to HEVC,
 * which hls.js only plays reliably in Safari while the CAD client is Chromium).
 */
export function buildHlsUrl(
    token: SensorStreamTokenResponse | undefined,
    sensorId: string | undefined
): string | undefined {
    if (!token?.hlsUrlTemplate || !sensorId) {
        return undefined;
    }
    return token.hlsUrlTemplate.replace(CAMERA_ID_PLACEHOLDER, encodeURIComponent(sensorId));
}

/**
 * Whether a newly mounted player should fetch a new token: only once the cached one is due. A new
 * token changes every feed's URL, which restarts every open stream.
 *
 * `'always'` rather than `true`, because `true` only refetches stale data and this query is never
 * stale (`staleTime: Infinity`). The first fetch doesn't depend on this: React Query loads a query
 * that has no data on mount regardless.
 */
export function shouldRefetchTokenOnMount(
    token: SensorStreamTokenResponse | undefined,
    nowMs: number
): 'always' | false {
    return token && millisUntilTokenRefresh(token, nowMs) > 0 ? false : 'always';
}

/**
 * One token covers every sensor until it is due for refresh, so every player shares it: React
 * Query dedupes the key, and a newly mounted player reuses the cached token rather than minting
 * another (see `shouldRefetchTokenOnMount`).
 *
 * This call fails whenever the environment is not fully provisioned — `cad.verkada.host` unset
 * makes it 500, for example. `shouldFailSilently` keeps that off the global error toast and the
 * caller falls back to the latest alert thumbnail, so an unprovisioned environment degrades to
 * stills rather than breaking the panel.
 *
 * `retry: false` matters here: the endpoint is uncached and calls the vendor on every request, so a
 * persistent misconfiguration should not become a retry storm against Verkada.
 */
export function useSensorStreamToken() {
    const apiClient = useApiClient();
    const sensorsEnabled = useSensorsEnabled();

    return useQuery({
        queryKey: ['sensor-stream-token'],
        async queryFn() {
            const response = await apiClient.request<SensorsResource.GetStreamToken>({
                method: 'POST',
                url: 'sensors/stream_token',
                data: { sensorType: SENSOR_TYPE_VERKADA_CAMERA },
                shouldFailSilently: true,
            });
            return response.data.data;
        },
        enabled: sensorsEnabled,
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        // The app-wide default is `refetchOnMount: 'always'`, which refetches for every new
        // observer whatever the `staleTime`, and every sensor panel and grid tile is one.
        refetchOnMount: (query) => shouldRefetchTokenOnMount(query.state.data, Date.now()),
        // Keep refreshing while a web tab is hidden, so its feeds come back to a valid token.
        refetchIntervalInBackground: true,
        refetchInterval(token) {
            if (!token) {
                return false;
            }
            return Math.max(
                millisUntilTokenRefresh(token, Date.now()),
                MIN_TOKEN_REFETCH_INTERVAL_MS
            );
        },
    });
}
